import { useState, useEffect } from "react";
import { subirDatos } from "./syncSupabase";
import { STORAGE_CUENTAS, asegurarPlanCuentasBase } from "./FinanzasConfig";

const TIPOS = {
  activo:     { label: 'Activo',      color: '#2A7FFF' },
  pasivo:     { label: 'Pasivo',      color: '#dc2626' },
  patrimonio: { label: 'Patrimonio',  color: '#7B5BF2' },
  ingreso:    { label: 'Ingreso',     color: '#16a34a' },
  egreso:     { label: 'Egreso',      color: '#d97706' },
};

const CUENTA_VACIA = { codigo: '', nombre: '', tipo: 'activo' };

function nivelDeCodigo(codigo) {
  const seg = (codigo || '').split('.');
  if (seg.length < 3) return 0;
  if (seg[1] === '0' && seg[2] === '00') return 0;
  if (seg[2] === '00') return 1;
  return 2;
}

export default function PlanCuentas({ onVolver, oscuro, usuario }) {
  const [cuentas, setCuentas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(CUENTA_VACIA);
  const [verInactivas, setVerInactivas] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    (async () => {
      const base = await asegurarPlanCuentasBase();
      setCuentas(base);
      setCargando(false);
    })();
    const onSync = () => {
      try { setCuentas(JSON.parse(localStorage.getItem(STORAGE_CUENTAS) || '[]')); } catch {}
    };
    window.addEventListener('supabase-sync-complete', onSync);
    return () => window.removeEventListener('supabase-sync-complete', onSync);
  }, []);

  const guardarLista = (lista) => {
    const ordenada = [...lista].sort((a, b) => (a.codigo || '').localeCompare(b.codigo || ''));
    setCuentas(ordenada);
    localStorage.setItem(STORAGE_CUENTAS, JSON.stringify(ordenada));
    subirDatos(STORAGE_CUENTAS);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const abrirNueva = () => { setEditando(null); setForm(CUENTA_VACIA); setShowForm(true); };
  const abrirEditar = (c) => { setEditando(c); setForm({ codigo: c.codigo, nombre: c.nombre, tipo: c.tipo }); setShowForm(true); };

  const guardar = () => {
    if (!form.codigo.trim()) return alert('El código es obligatorio');
    if (!form.nombre.trim()) return alert('El nombre es obligatorio');
    const duplicado = cuentas.find(c => c.codigo === form.codigo.trim() && c.id !== editando?.id);
    if (duplicado) return alert(`Ya existe una cuenta con el código ${form.codigo}`);

    if (editando) {
      const lista = cuentas.map(c => c.id === editando.id ? { ...c, ...form, codigo: form.codigo.trim() } : c);
      guardarLista(lista);
    } else {
      const nueva = { ...form, codigo: form.codigo.trim(), id: Math.floor(Date.now()), saldo: 0, parentId: null, activo: true };
      guardarLista([...cuentas, nueva]);
    }
    setShowForm(false); setEditando(null); setForm(CUENTA_VACIA);
  };

  const toggleActiva = async (c) => {
    const lista = cuentas.map(x => x.id === c.id ? { ...x, activo: !x.activo } : x);
    guardarLista(lista);
  };

  const inp = { width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' };
  const lbl = { fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px', display: 'block' };

  const visibles = cuentas.filter(c => verInactivas || c.activo !== false);

  if (cargando) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Cargando plan de cuentas…</div>;
  }

  return (
    <div style={{ padding: '24px', fontFamily: 'Inter, sans-serif', background: '#E8E9F6', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={onVolver} style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', color: '#6B7280' }}>← Menú</button>
          <span style={{ fontSize: '22px' }}>📒</span>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>Plan de Cuentas</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6B7280', cursor: 'pointer' }}>
            <input type="checkbox" checked={verInactivas} onChange={e => setVerInactivas(e.target.checked)} /> Ver inactivas
          </label>
          <button onClick={abrirNueva} style={{ background: '#1C3FAA', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 18px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>+ Nueva cuenta</button>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Código', 'Nombre', 'Tipo', 'Saldo', 'Estado', ''].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontSize: '11px', fontWeight: '600', color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibles.map(c => (
              <tr key={c.id} style={{ opacity: c.activo === false ? 0.5 : 1 }}>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontFamily: 'monospace', fontSize: '12px', paddingLeft: `${14 + nivelDeCodigo(c.codigo) * 20}px` }}>{c.codigo}</td>
                <td onClick={() => abrirEditar(c)} style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: nivelDeCodigo(c.codigo) === 0 ? '700' : '500', fontSize: '13px', color: '#111827', cursor: 'pointer' }}>{c.nombre}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ background: `${TIPOS[c.tipo]?.color}18`, color: TIPOS[c.tipo]?.color, fontSize: '11px', padding: '2px 8px', borderRadius: '6px', fontWeight: '600' }}>{TIPOS[c.tipo]?.label || c.tipo}</span>
                </td>
                <td style={{ padding: '11px 14px', fontSize: '12px', color: '#374151', borderBottom: '1px solid #F3F4F6' }}>${Number(c.saldo || 0).toLocaleString('es-AR')}</td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: c.activo === false ? '#dc2626' : '#16a34a' }}>{c.activo === false ? 'Inactiva' : 'Activa'}</span>
                </td>
                <td style={{ padding: '11px 14px', borderBottom: '1px solid #F3F4F6' }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => toggleActiva(c)} style={{ background: 'none', border: '1px solid #E5E7EB', color: '#6B7280', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                    {c.activo === false ? 'Reactivar' : 'Dar de baja'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '420px', padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1C3FAA', margin: 0 }}>{editando ? '✏️ Editar cuenta' : '+ Nueva cuenta'}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={lbl}>Código *</label>
                <input style={inp} placeholder="Ej: 1.1.04" value={form.codigo} onChange={e => set('codigo', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Nombre *</label>
                <input style={inp} placeholder="Ej: Anticipos a Proveedores" value={form.nombre} onChange={e => set('nombre', e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Tipo</label>
                <select style={inp} value={form.tipo} onChange={e => set('tipo', e.target.value)}>
                  {Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowForm(false)} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #E5E7EB', background: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#6B7280' }}>Cancelar</button>
              <button onClick={guardar} style={{ flex: 2, padding: '10px', borderRadius: '10px', border: 'none', background: '#1C3FAA', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
